import { useEffect, useState } from "react";

const FN_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twitch-search`;
const API_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

export type TwitchChannel = {
  id: string;
  login: string;
  name: string;
  thumb?: string;
  gameName?: string;
  isLive?: boolean;
  title?: string;
};

export type TwitchUser = {
  id: string;
  login: string;
  name: string;
  description?: string;
  thumb?: string;
  offlineThumb?: string;
  viewCount: number;
  createdAt?: string;
  isLive: boolean;
  liveViewers: number;
};

const headers = { apikey: API_KEY, Authorization: `Bearer ${API_KEY}` };

export const useTwitchSearch = (query: string) => {
  const [channels, setChannels] = useState<TwitchChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setChannels([]);
      setSearched(false);
      setStale(false);
      return;
    }
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${FN_BASE}?action=search&q=${encodeURIComponent(q)}`, { headers });
        const data = await res.json();
        setChannels(data.channels || []);
        setStale(Boolean(data?._stale));
      } catch (e) {
        console.error("twitch search:", e);
        setChannels([]);
        setStale(false);
      } finally {
        setLoading(false);
        setSearched(true);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [query]);

  return { channels, loading, searched, stale };
};

export const useTwitchUser = (login: string) => {
  const [user, setUser] = useState<TwitchUser | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!login) {
      setUser(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${FN_BASE}?action=user&login=${encodeURIComponent(login)}`, { headers });
        const data = await res.json();
        if (!cancelled) setUser(data.user || null);
      } catch (e) {
        console.error("twitch user:", e);
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [login]);

  return { user, loading };
};

export const parseTwitchUrl = (input: string): string | null => {
  const trimmed = input.trim().replace(/^@/, "");
  if (!trimmed) return null;
  // URL form: https://www.twitch.tv/login
  const m = trimmed.match(/twitch\.tv\/([a-zA-Z0-9_]+)/i);
  if (m) return m[1].toLowerCase();
  // Plain handle
  if (/^[a-zA-Z0-9_]{2,30}$/.test(trimmed)) return trimmed.toLowerCase();
  return null;
};
